import { Suspense, lazy, useEffect, type ComponentType, type ReactNode } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';

import AppShell from '@/components/AppShell';
import Login from '@/pages/Login';
import { useAuthStore, useIsPlatformAdmin } from '@/store/auth';

/**
 * Маршруты панели.
 *
 * Раскладка: /login снаружи, всё остальное — под проверкой сессии и внутри
 * каркаса AppShell. Незалогиненного уводим на /login, ЗАПОМНИВ исходный путь:
 * оператору кидают ссылку на конкретный диалог, и после входа он обязан
 * попасть именно в него, а не в общий список.
 */

/**
 * Экраны подключаются по имени файла, а не прямым импортом.
 *
 * Панель собирают несколько человек параллельно, и прямой `import` на ещё не
 * написанный экран роняет сборку целиком — вместе с уже готовыми разделами.
 * Глоб отдаёт то, что реально лежит в pages/, а недостающее заменяет
 * заглушкой; как только файл появляется, он подхватывается сам, без правок
 * здесь.
 *
 * Контракт имён (default-экспорт React-компонента):
 *   pages/Chat.tsx    — /inbox и /inbox/:conversationId
 *   pages/Storage.tsx — /storage/* (свои вложенные маршруты внутри)
 *   pages/Stats.tsx   — /stats
 *   pages/Admin.tsx   — /admin/* (свои вложенные маршруты внутри)
 */
type PageModule = { default: ComponentType };
const pages = import.meta.glob([
  './pages/Chat.tsx',
  './pages/Storage.tsx',
  './pages/Stats.tsx',
  './pages/Admin.tsx',
]) as Record<string, () => Promise<unknown>>;

function page(name: string): ComponentType {
  const loader = pages[`./pages/${name}.tsx`];
  if (!loader) return () => <Pending name={name} />;
  return lazy(() => loader() as Promise<PageModule>);
}

const Chat = page('Chat');
const Storage = page('Storage');
const Stats = page('Stats');
const Admin = page('Admin');

export default function App(): ReactNode {
  const bootstrap = useAuthStore((state) => state.bootstrap);

  // Восстановление сессии — один раз на загрузку вкладки. Сам bootstrap
  // защищён от повторного вызова: React 19 в StrictMode монтирует эффекты
  // дважды, а ротация refresh-токена одноразовая.
  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  return (
    <Suspense fallback={<Splash />}>
      <Routes>
        <Route path="/login" element={<Login />} />

        <Route element={<RequireAuth />}>
          <Route element={<AppShell />}>
            <Route index element={<Navigate to="/inbox" replace />} />
            <Route path="inbox" element={<Chat />} />
            <Route path="inbox/:conversationId" element={<Chat />} />
            <Route path="storage/*" element={<Storage />} />
            <Route path="stats" element={<Stats />} />
            <Route
              path="admin/*"
              element={
                <RequirePlatformAdmin>
                  <Admin />
                </RequirePlatformAdmin>
              }
            />
            {/* Неизвестный путь — в инбокс: это рабочий экран по умолчанию. */}
            <Route path="*" element={<Navigate to="/inbox" replace />} />
          </Route>
        </Route>
      </Routes>
    </Suspense>
  );
}

// ──────────────────────────── Защита маршрутов ────────────────────────────

function RequireAuth(): ReactNode {
  const status = useAuthStore((state) => state.status);
  const location = useLocation();

  // Пока сессия восстанавливается, редиректить нельзя: иначе при каждой
  // перезагрузке оператор видит вспышку формы входа, хотя он уже вошёл.
  if (status === 'loading') return <Splash />;

  if (status === 'anonymous') {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }

  return <Outlet />;
}

/**
 * Администрирование платформы.
 *
 * Проверка на фронте — про то, что показывать: отказ всё равно выдаёт API.
 * Но вести оператора на экран, который гарантированно вернёт 403, нельзя —
 * он решит, что панель сломалась.
 */
function RequirePlatformAdmin({ children }: { children: ReactNode }): ReactNode {
  const isAdmin = useIsPlatformAdmin();
  if (!isAdmin) return <Navigate to="/inbox" replace />;
  return children;
}

// ──────────────────────────── Заглушки ────────────────────────────

function Splash(): ReactNode {
  return (
    <div style={{ height: '100%', display: 'grid', placeItems: 'center', color: 'var(--text-muted)' }}>
      Загрузка…
    </div>
  );
}

/** Экран ещё не написан. Видно только в разработке — в проде файл на месте. */
function Pending({ name }: { name: string }): ReactNode {
  return (
    <div className="storage-empty">
      <div className="se-title">Раздел готовится</div>
      <div className="se-text">Экран {name} ещё не подключён.</div>
    </div>
  );
}
